import React, { useState } from 'react';
import { triggerPaymentEmail, downLoadQrCode } from '../../src/store/slices/insurance';
import { useParams } from 'react-router-dom';
import FullPageLoader from '../../src/common/FullPageLoader';
import moment from 'moment';

type EmiInstallmentActionsProps = {
    shouldShow: boolean;
    isOverPaid: boolean;
    isCurrentPaid: boolean;
    installmentNo?: number;
    qrCodeDetails?: QrCodeDetails | null;
    reloadDetails?: () => void;
    setLeadDetailLoading?: (isLoading: boolean) => void;
};

type QrCodeDetails = {
    last_sent_mail?: string | null;
    mail_sent_by_name?: string | null;
    qr_code?: string | null;
    is_qrcode_generated?: number | boolean | null;
    qrcode_last_use?: string | null;
};

type ActionCardContentProps = {
    defaultMessage: string;
    isActionDisabled: boolean;
    isActionOverPaid: boolean;
};

const ActionCardContent = ({ defaultMessage, isActionDisabled, isActionOverPaid }: ActionCardContentProps) => {
    if (isActionOverPaid) {
        return (
            <>
                <p className="emi-action-card-status">Excess payment received</p>
                <p>No further action allowed</p>
            </>
        );
    }

    if (isActionDisabled) {
        return (
            <>
                <p className="emi-action-card-status">Payment completed</p>
                <p>No further action required</p>
            </>
        );
    }

    return <p>{defaultMessage}</p>;
};

const EmiInstallmentActions = ({ shouldShow, isOverPaid, isCurrentPaid, installmentNo, qrCodeDetails, reloadDetails, setLeadDetailLoading }: EmiInstallmentActionsProps) => {
    const { id } = useParams<{ id: string }>();
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [isDownloadingQr, setIsDownloadingQr] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const isLeadDetailLoaderControlled = typeof setLeadDetailLoading === 'function';
    const setPageLoading = (isLoading: boolean) => {
        setLoading(isLoading);
        setLeadDetailLoading?.(isLoading);
    };

    if (!shouldShow) return null;

    const emiActionCardViewState = isOverPaid ? "overpaid" : isCurrentPaid ? "disabled" : "default";
    const isInstallmentActionDisabled = emiActionCardViewState !== "default";
    const isInstallmentActionOverPaid = emiActionCardViewState === "overpaid";
    const emiActionCardStateClass = isInstallmentActionOverPaid ? "emi-action-card-overpaid" : isInstallmentActionDisabled ? "emi-action-card-disabled" : "";
    const actionPayload = { lead_id: id };

    const getQrDownloadUrl = (response: any) => response?.data?.url;

    const formatMetaDate = (dateValue?: string | null) => {
        if (!dateValue) return "Never";
        const date = moment(dateValue);
        return date.isValid() ? date.format("DD MMM, YYYY hh:mm A") : "Never";
    };

    const isQrGenerated = Number(qrCodeDetails?.is_qrcode_generated) === 1 || Boolean(qrCodeDetails?.qr_code);

    const downloadQrFile = async (qrUrl: string) => {
        const qrFileName = qrUrl.split('?')?.[0]?.split('/')?.pop() || `emi-${installmentNo || 1}-qr`;
        const fileResponse = await fetch(qrUrl);
        const fileBlob = await fileResponse.blob();
        const fileObjectUrl = URL.createObjectURL(fileBlob);
        const link = document.createElement('a');

        link.href = fileObjectUrl;
        link.download = qrFileName;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(fileObjectUrl);
    };

    const handleTriggerEmail = async () => {
        if (!id || isInstallmentActionDisabled || isSendingEmail || loading) {
            return;
        }

        try {
            setPageLoading(true);
            setIsSendingEmail(true);
            await triggerPaymentEmail(actionPayload);
            reloadDetails?.();
        } catch (error) {
            console.error("Error while triggering payment email:", error);
        } finally {
            setIsSendingEmail(false);
            setPageLoading(false);
        }
    };

    const handleDownloadQr = async () => {
        if (!id || isInstallmentActionDisabled || isDownloadingQr || loading) {
            return;
        }

        try {
            setPageLoading(true);
            setIsDownloadingQr(true);
            const response: any = await downLoadQrCode(actionPayload);
            const qrUrl = getQrDownloadUrl(response);

            if (qrUrl) {
                await downloadQrFile(qrUrl);
                reloadDetails?.();
            }
        } catch (error) {
            console.error("Error while downloading QR code:", error);
        } finally {
            setIsDownloadingQr(false);
            setPageLoading(false);
        }
    };

    return (
        <>
            {!isLeadDetailLoaderControlled && <FullPageLoader show={loading} />}
            <div className="emi-detail-actions">
                <div className={`emi-action-card emi-action-card-email ${emiActionCardStateClass}`}>
                    <div className="emi-action-card-header">
                        {isInstallmentActionOverPaid ? (
                            <span className="emi-action-card-icon emi-action-card-icon-warning" aria-hidden="true">
                                <svg viewBox="0 0 56 48" focusable="false">
                                    <path d="M18.5 6.5 10 34.5c-.5 1.8-2.2 2.9-4 2.7l-3.5-.4 9.2-29.6c.8-2.5 3.3-3.9 5.8-3.3Z" fill="#2C3440" />
                                    <path d="M37.5 6.5 46 34.5c.5 1.8 2.2 2.9 4 2.7l3.5-.4-9.2-29.6c-.8-2.5-3.3-3.9-5.8-3.3Z" fill="#2C3440" />
                                    <path d="M28 4c2.6 0 5 1.4 6.3 3.6l13.3 23c2.6 4.4-.6 9.9-5.7 9.9H14.1c-5.1 0-8.3-5.5-5.7-9.9l13.3-23A7.24 7.24 0 0 1 28 4Z" fill="#FFBC1F" />
                                    <path d="M28 13v12.4" stroke="#FFFFFF" strokeWidth="3.4" strokeLinecap="round" />
                                    <circle cx="28" cy="31.2" r="2.1" fill="#FFFFFF" />
                                </svg>
                            </span>
                        ) : (
                            <span className="emi-action-card-icon">
                                <i className="ic-email"></i>
                            </span>
                        )}
                        <div className="emi-action-card-content">
                            <h4>Trigger Payment Email</h4>
                            <ActionCardContent defaultMessage="Send QR payment link to customer" isActionDisabled={isInstallmentActionDisabled} isActionOverPaid={isInstallmentActionOverPaid} />
                        </div>
                    </div>
                    <button
                        type="button"
                        className="emi-action-card-btn"
                        disabled={isInstallmentActionDisabled || isSendingEmail || loading}
                        onClick={handleTriggerEmail}
                    >
                        <span>{isSendingEmail ? "Sending..." : "Send Email"}</span>
                        <span className="emi-action-card-btn-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" focusable="false">
                                <path d="M21.62 3.38a1 1 0 0 0-1.05-.24l-17 6a1 1 0 0 0 .08 1.91l7.02 1.95 1.95 7.02a1 1 0 0 0 .8.72h.15a1 1 0 0 0 .89-.54l7-16.99a1 1 0 0 0-.24-1.05Zm-7.67 13.77-1.28-4.62 4.54-4.53-5.88 3.69-4.63-1.29 11.61-4.09-4.36 10.84Z" />
                            </svg>
                        </span>
                    </button>
                    <div className="emi-action-card-meta">
                        <p>
                            <span>Last sent:</span>
                            <strong>{formatMetaDate(qrCodeDetails?.last_sent_mail)}</strong>
                        </p>
                        <p>
                            <span>Sent by:</span>
                            <strong>{qrCodeDetails?.mail_sent_by_name || "-"}</strong>
                        </p>
                    </div>
                </div>
                <div className={`emi-action-card emi-action-card-qr ${emiActionCardStateClass}`}>
                    <div className="emi-action-card-header">
                        <span className="emi-action-card-icon emi-action-card-icon-qr" aria-hidden="true">
                            <svg viewBox="0 0 24 24" focusable="false">
                                <path d="M3 3h7v7H3V3Zm2 2v3h3V5H5Zm9-2h7v7h-7V3Zm2 2v3h3V5h-3ZM3 14h7v7H3v-7Zm2 2v3h3v-3H5Zm6-5h2v2h-2v-2Zm3 0h1v1h-1v-1Zm2 0h2v2h-2v-2Zm-2 3h2v2h-2v-2Zm3 2h1v1h-1v-1Zm-6 1h2v2h-2v-2Zm3 0h1v1h-1v-1Zm2 0h1v1h-1v-1Zm2 0h2v2h-2v-2Zm-5 3h2v1h-2v-1Zm5-1h1v2h-1v-2Z" fill="currentColor" />
                            </svg>
                        </span>
                        <div className="emi-action-card-content">
                            <h4>Download QR</h4>
                            <ActionCardContent defaultMessage="Customer can scan & pay instantly" isActionDisabled={isInstallmentActionDisabled} isActionOverPaid={isInstallmentActionOverPaid} />
                        </div>
                    </div>
                    <button
                        type="button"
                        className="emi-action-card-btn"
                        disabled={isInstallmentActionDisabled || isDownloadingQr || loading}
                        onClick={handleDownloadQr}
                    >
                        <span>{isDownloadingQr ? "Downloading..." : "Download QR"}</span>
                        <i className="ic-save_alt"></i>
                    </button>
                    <div className="emi-action-card-meta">
                        <p>
                            <span>QR Generated:</span>
                            <strong>{isQrGenerated ? "Yes" : "No"}</strong>
                        </p>
                        <p>
                            <span>Last used:</span>
                            <strong>{formatMetaDate(qrCodeDetails?.qrcode_last_use)}</strong>
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EmiInstallmentActions;
